import { Inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { AnalyticsInstance } from 'analytics';
import { ANALYTICS_INSTANCE } from '../app-init.service';

@Injectable({
  providedIn: 'root',
})
export class FirebaseAnalyticsService {
  constructor(
    @Inject(ANALYTICS_INSTANCE) private readonly analytics: AnalyticsInstance
  ) {}

  /**
   * Send an analytics event to Google Analytics using gtag.js.
   * Defers the gtag call to the next event loop cycle to minimize performance impact.
   * @param eventaction The action that triggered the event
   * @param category The event category
   * @param label The event label
   * @param catId Optional category ID for custom events
   * @param eventID Optional event ID for custom events
   */
  sendAction(
    eventaction: string,
    category: string,
    label: string | number,
    catId?: any,
    eventID?: any
  ): void {
    // Check if analytics instance is available
    if (!this.analytics) {
      if (!environment.production) {
        console.error(
          'Analytics instance not found. Analytics event not sent.'
        );
      }
      return;
    }

    if (category !== undefined && label !== undefined) {
      try {
        if (!environment.production) {
          console.log('Sending analytics event:', {
            eventaction,
            category,
            label,
          });
        }

        this.analytics.track(`${category}-${label}`, {
          event_category: category,
          event_label: label,
          action: eventaction,
        });
      } catch (error) {
        if (!environment.production) {
          console.error('Error sending analytics event:', error, {
            eventaction,
            category,
            label,
          });
        }
      }
    }

    // Send custom event with IDs if provided
    // Note: Ensure the frequency of calls is managed (e.g., debouncing/throttling in the calling component).
    if (catId !== undefined && eventID !== undefined) {
      try {
        this.analytics.track('custom_event_with_id', {
          category_id: catId,
          event_identifier: eventID,
          original_action: eventaction,
        });
      } catch (error) {
        if (!environment.production) {
          // Log error only in development
          console.error(
            'Error sending custom analytics event via analytics.track:',
            error,
            { eventaction, catId, eventID }
          );
        }
      }
    }
  }

  public triggerFirebase(
    firebaseKey: Record<string, any>,
    prop: string | number
  ) {
    this.sendAction(
      firebaseKey[prop].eventAction,
      firebaseKey[prop].category,
      firebaseKey[prop].Label,
      firebaseKey[prop].catId,
      firebaseKey[prop].eventId
    );
  }
}
